import { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { Card, CardHeader, Button, Badge, Spinner, EmptyState, ErrorText } from "../../components/ui";
import { StatusBadge } from "../../components/StatusBadge";
import { date, dateTime, km, money } from "../../lib/format";
import type { CaseRow, VehicleRow, DocumentRow, PaymentRow, HistoryRow, Page } from "../../lib/types";

export function PortalCaseDetail() {
  const { id = "" } = useParams();
  const base = `/api/portal/cases/${id}`;
  const caseQ = useApi<CaseRow>(base);
  const vehicleQ = useApi<VehicleRow>(`${base}/vehicle`);
  const docsQ = useApi<Page<DocumentRow>>(`${base}/documents`);
  const reportQ = useApi<{ status: string; versions: { version: number }[] }>(`${base}/report`);
  const paymentsQ = useApi<Page<PaymentRow>>(`${base}/payments`);
  const historyQ = useApi<Page<HistoryRow>>(`${base}/history`);

  if (caseQ.loading) return <Spinner />;
  if (caseQ.error || !caseQ.data) return <EmptyState>No se encontró el expediente.</EmptyState>;
  const c = caseQ.data;

  return (
    <>
      <div className="mb-6">
        <Link to="/cases" className="text-sm text-muted hover:underline">← Expedientes</Link>
        <div className="mt-2 flex items-center gap-3">
          <h1 className="font-mono text-2xl font-semibold tracking-tight">{c.publicRef}</h1>
          <StatusBadge status={c.status} />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="flex flex-col gap-6 lg:col-span-2">
          <DocumentsCard caseId={id} query={docsQ} />
          <ReportCard caseId={id} report={reportQ.data} loading={reportQ.loading} />
          <PaymentsCard payments={paymentsQ.data?.data ?? []} />
        </div>

        <div className="flex flex-col gap-6">
          {vehicleQ.data && <VehicleCard vehicle={vehicleQ.data} price={money(c.priceMinor, c.currency ?? "EUR")} country={c.country} />}
          <TimelineCard history={historyQ.data?.data ?? []} />
        </div>
      </div>
    </>
  );
}

function VehicleCard({ vehicle, price, country }: { vehicle: VehicleRow; price: string; country: string }) {
  return (
    <Card>
      <CardHeader title="Vehículo" />
      <dl className="divide-y divide-line text-sm">
        <Row k="Marca / modelo" v={`${vehicle.make} ${vehicle.model}`} />
        <Row k="Año" v={vehicle.year ?? "—"} />
        <Row k="VIN" v={vehicle.vin ?? "—"} mono />
        <Row k="Kilómetros" v={km(vehicle.mileageKm)} />
        <Row k="País de inspección" v={country} />
        <Row k="Importe" v={price} />
      </dl>
    </Card>
  );
}

function Row({ k, v, mono }: { k: string; v: string | number; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between px-5 py-2.5">
      <dt className="text-muted">{k}</dt>
      <dd className={mono ? "font-mono tnum" : "tnum"}>{v}</dd>
    </div>
  );
}

function DocumentsCard({ caseId, query }: { caseId: string; query: ReturnType<typeof useApi<Page<DocumentRow>>> }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const docs = query.data?.data ?? [];

  async function onFile(file: File) {
    setBusy(true);
    setError(null);
    try {
      await api.upload(`/api/portal/cases/${caseId}/documents`, file);
      await query.reload();
    } catch {
      setError("No se pudo subir el archivo (revisa tipo y tamaño).");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <Card>
      <CardHeader
        title="Documentos"
        action={
          <>
            <input ref={inputRef} type="file" hidden accept="application/pdf,image/*" onChange={(e) => e.target.files?.[0] && void onFile(e.target.files[0])} />
            <Button variant="ghost" disabled={busy} onClick={() => inputRef.current?.click()}>{busy ? "Subiendo…" : "Subir documento"}</Button>
          </>
        }
      />
      {error && <div className="px-5 pt-4"><ErrorText>{error}</ErrorText></div>}
      {docs.length === 0 ? (
        <div className="p-6 text-sm text-muted">Sube aquí la documentación del vehículo (ficha técnica, permiso, anuncio…).</div>
      ) : (
        <ul className="divide-y divide-line">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between px-5 py-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{d.originalFilename}</div>
                <div className="text-xs text-muted">{date(d.createdAt)} · {(d.sizeBytes / 1024).toFixed(0)} KB</div>
              </div>
              <div className="flex items-center gap-3">
                <Badge tone={d.status === "APPROVED" ? "ok" : d.status === "REJECTED" ? "danger" : "warn"}>{docStatus(d.status)}</Badge>
                <button onClick={() => void api.download(`/api/portal/documents/${d.id}/content`, d.originalFilename)} className="text-sm font-medium text-accent-600 hover:underline">Descargar</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ReportCard({ caseId, report, loading }: { caseId: string; report: { status: string; versions: { version: number }[] } | null; loading: boolean }) {
  const published = report?.status === "PUBLISHED";
  return (
    <Card>
      <CardHeader title="Informe de inspección" />
      <div className="flex items-center justify-between px-5 py-4">
        {loading ? (
          <Spinner />
        ) : published ? (
          <>
            <div className="text-sm text-muted">Informe disponible (v{report?.versions[0]?.version ?? 1}).</div>
            <div className="flex items-center gap-3">
              <Link to={`/cases/${caseId}/inspection`} className="btn btn-ghost">Ver inspección</Link>
              <Button onClick={() => api.openTab(`/api/portal/cases/${caseId}/report/html`)}>Ver informe</Button>
              <button onClick={() => void api.download(`/api/portal/cases/${caseId}/report/pdf`, `informe-${caseId}.pdf`)} className="text-sm text-muted hover:underline">PDF</button>
            </div>
          </>
        ) : (
          <div className="text-sm text-muted">El informe estará disponible cuando finalice la inspección.</div>
        )}
      </div>
    </Card>
  );
}

function PaymentsCard({ payments }: { payments: PaymentRow[] }) {
  return (
    <Card>
      <CardHeader title="Pagos" />
      {payments.length === 0 ? (
        <div className="p-6 text-sm text-muted">Sin pagos registrados.</div>
      ) : (
        <ul className="divide-y divide-line">
          {payments.map((p) => (
            <li key={p.id} className="flex items-center justify-between px-5 py-3 text-sm">
              <span>{p.method ?? "Pago"} {p.reference ? `· ${p.reference}` : ""}</span>
              <span className="flex items-center gap-3">
                <span className="tnum font-medium">{money(p.amountMinor, p.currency)}</span>
                <Badge tone={p.status === "SUCCEEDED" ? "ok" : "warn"}>{p.status}</Badge>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function TimelineCard({ history }: { history: HistoryRow[] }) {
  return (
    <Card>
      <CardHeader title="Cronología" />
      {history.length === 0 ? (
        <div className="p-6 text-sm text-muted">Sin actividad.</div>
      ) : (
        <ol className="flex flex-col gap-4 px-5 py-4">
          {history.map((h) => (
            <li key={h.id} className="flex gap-3">
              <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-accent" />
              <div>
                <div className="text-sm"><StatusBadge status={h.toStatus} /></div>
                <div className="mt-0.5 text-xs text-muted">{dateTime(h.createdAt)}</div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function docStatus(s: DocumentRow["status"]): string {
  return s === "APPROVED" ? "Aprobado" : s === "REJECTED" ? "Rechazado" : s === "SUPERSEDED" ? "Reemplazado" : "En revisión";
}
